import React from 'react'
import classNames from 'classnames'
import { connect } from 'react-redux'
import {
    AnchorButton,
    Button,
    Drawer,
    InputGroup,
    Tab,
    Tag,
} from '@blueprintjs/core'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faPaperPlane } from '@fortawesome/pro-solid-svg-icons/faPaperPlane'
import { faUpRightFromSquare } from '@fortawesome/pro-solid-svg-icons/faUpRightFromSquare'

import ButtonTabs from 'src/components/ButtonTabs'
import Comments from 'src/components/Comments'
import { SD_URL, MON_URL } from 'src/constants/urls'
import { getRunbook } from 'src/store/modules/runbooks'
import {
    getAction,
    getActionDevice,
    getActionLogsFromServer,
    focusAction,
    isDevice,
    isSolution,
    isVirp,
    addCommentToAction,
} from 'src/store/modules/runbooks/actions'
import {
    singleEditAction,
    toggleControl,
} from 'src/store/modules/ui/runbook/controls'

import styles from './actionDetailsDrawer.module.scss'

const usdLink = (ci) => `${SD_URL}${ci}`
const monLink = (action, parent) => {
    if (isVirp(action)) {
        let nameArr = action.name.split(' ')
        let pg = nameArr[nameArr.length - 1]
        return `${MON_URL}/customer/${action.customerId}/solution-infra/${action.device}/?pg=${pg}`
    }
    if (isDevice(action)) {
        return `${MON_URL}/device/${action.device}/`
    }
    if (!action.isAvZone) {
        return `${MON_URL}/customer/${action.customerId}/solution-infra/${action.device}/`
    } else if (parent) {
        return `${MON_URL}/customer/${action.customerId}/solution-infra/${parent.device}/${action.name}/?avzone=true`
    }
}

class ActionDetailsDrawer extends React.Component {
    state = {
        newComment: '',
        activeTab: '',
        isScrolledTop: true,
        alreadyListenToScroll: false,
        initialScrollToBottom: false,
    }

    commentsWrapperRef = React.createRef()

    componentDidMount() {
        const {
            action,
            activeSession,
            getActionLogsFromServer,
            activeActionDetailsDrawerTab,
        } = this.props

        if (activeSession && action && action.id) {
            getActionLogsFromServer(activeSession, action.uid)
        }

        this.setState({ activeTab: activeActionDetailsDrawerTab })
    }

    componentDidUpdate(prevProps, prevState) {
        const {
            action,
            activeSession,
            getActionLogsFromServer,
            actionLogs,
        } = this.props
        const {
            activeTab,
            alreadyListenToScroll,
            initialScrollToBottom,
        } = this.state

        // Get updated comments list when user add a new comment
        if (activeSession && action && action.id) {
            if (
                (prevProps.action && prevProps.action.id !== action.id) ||
                prevProps.action.status !== action.status
            ) {
                getActionLogsFromServer(activeSession, action.uid)
            }
        }

        // Scroll to bottom when use click on the activity tab
        if (
            activeTab !== prevState.activeTab &&
            activeTab === 'activity' &&
            this.commentsWrapperRef.current
        ) {
            this.scrollToBottom()
        }

        // Add an event listener on scroll for the comments section. This was done
        // in componentDidUpdate because in componentDidMount the [commentsWrapperRef.current]
        // may be null
        if (
            this.commentsWrapperRef.current !== null &&
            !alreadyListenToScroll
        ) {
            this.commentsWrapperRef.current.addEventListener(
                'scroll',
                this.listenToCommentsScroll
            )
            this.setState({ alreadyListenToScroll: true })
        }

        // Scroll to bottom on new added comment
        if (
            prevProps.actionLogs &&
            actionLogs?.length !== prevProps?.actionLogs.length
        ) {
            this.scrollToBottom()
        }

        // Scroll to bottom when the user opens the comments section directly from
        // the action menu and not by clicking the action name
        if (
            !initialScrollToBottom &&
            this.commentsWrapperRef.current &&
            this.commentsWrapperRef.current.scrollHeight > 0
        ) {
            this.scrollToBottom()
            this.setState({ initialScrollToBottom: true })
        }
    }

    componentWillUnmount() {
        this.commentsWrapperRef?.current?.removeEventListener(
            'scroll',
            this.listenToCommentsScroll
        )
    }

    handleActionTagClick = (uid) => () => {
        this.props.focusAction(this.props.activeRunbook, uid)
    }

    handleDrawerClose = () => {
        const { singleEditAction, toggleControl } = this.props

        singleEditAction(null)
        toggleControl('isDrawerOpen')
    }

    handleAddNewComment = () => {
        const { newComment } = this.state
        const {
            runbook,
            activeSession,
            singleOpenAction,
            addCommentToAction,
        } = this.props

        if (newComment) {
            addCommentToAction(
                runbook.activeRunbook,
                singleOpenAction,
                newComment,
                activeSession
            )

            this.setState({ newComment: '' })
        }
    }

    getUsernameInitials(fullName) {
        return fullName
            .split(' ')
            .map((n) => n[0])
            .join('')
            .toUpperCase()
    }

    parseComments = (comments) => {
        return comments.map((comment) => {
            const fullUserName = comment.createdBy ?  `${comment.createdBy?.first_name} ${comment.createdBy?.last_name}` : 'System'

            return {
                title: fullUserName,
                subtitle: comment.createdAt,
                message: comment.message,
                avatar: this.getUsernameInitials(fullUserName),
            }
        })
    }

    scrollToBottom = () => {
        if (this.commentsWrapperRef.current)
            this.commentsWrapperRef.current.scrollTo(
                0,
                this.commentsWrapperRef.current.scrollHeight
            )
    }

    listenToCommentsScroll = () => {
        const { scrollTop } = this.commentsWrapperRef.current

        if (scrollTop === 0) {
            this.setState({
                isScrolledTop: true,
            })
        } else {
            this.setState({ isScrolledTop: false })
        }
    }

    _renderButtonLinks = () => {
        const { action, treeParent } = this.props

        return (
            <div className={styles.externalLinksContainer}>
                {action.device ? (
                    <AnchorButton
                        intent="tarawera"
                        rightIcon={<FontAwesomeIcon icon={faUpRightFromSquare} />}
                        href={usdLink(action.device)}
                        className={styles.externalLinkButton}
                        target="_blank"
                        rel="noopener noreferrer"
                    >
                        View CMDB
                    </AnchorButton>
                ) : null}

                {(action.device || action.isAvZone) &&
                (action.monitored ||
                    action.isAvZone ||
                    isVirp(action) ||
                    isSolution(action)) ? (
                    <AnchorButton
                        intent="tarawera"
                        rightIcon={<FontAwesomeIcon icon={faUpRightFromSquare} />}
                        href={monLink(action, treeParent)}
                        className={styles.externalLinkButton}
                        target="_blank"
                        rel="noopener noreferrer"
                    >
                        View Mon
                    </AnchorButton>
                ) : null}
            </div>
        )
    }

    _renderDetailsTabContent = () => {
        const { action, actionDevice } = this.props
        const sections = [
            [
                {
                    title: 'Organization',
                    value: action.customer,
                },
                {
                    title: 'Hostname',
                    value: action.name,
                },
            ],
            [
                {
                    title: 'Est. Duration',
                    value: action.estimatedDuration,
                },

                {
                    title: 'Parent',
                    value: action.treeParent,
                },
            ],
            [
                {
                    title: 'Class',
                    value: action.deviceClass,
                },
                {
                    title: 'Solutions',
                    value: action.device ? <p>{action.device}</p> : undefined,
                },
            ],
            [
                {
                    title: 'Availability Zones',
                    value:
                        actionDevice &&
                        actionDevice.details.availability_zones ? (
                            <span>
                                {actionDevice.details.availability_zones.join(
                                    ','
                                )}
                            </span>
                        ) : undefined,
                },
                {
                    title: 'Main Availibility Zone',
                    value:
                        actionDevice &&
                        actionDevice.details.main_availability_zones ? (
                            <span>
                                {actionDevice.details.main_availability_zones.join(
                                    ','
                                )}
                            </span>
                        ) : undefined,
                },
            ],
            [
                {
                    title: 'Location',
                    value:
                        actionDevice && actionDevice.details.locations ? (
                            <span>
                                {actionDevice.details.locations.join(',')}
                            </span>
                        ) : undefined,
                },
                {
                    title: 'Dependencies',
                    value:
                        action.parents.length !== 0
                            ? action.parents.map((dependency) => (
                                  <Tag
                                      interactive
                                      style={{ marginRight: 2 }}
                                      key={dependency}
                                      onClick={this.handleActionTagClick(
                                          dependency
                                      )}
                                  >
                                      {dependency}
                                  </Tag>
                              ))
                            : 'n/a',
                },
            ],
            [
                {
                    title: 'Dr Active',
                    value:
                        actionDevice && actionDevice.details.dr_active === null
                            ? 'No info'
                            : actionDevice && actionDevice.details.dr_active
                            ? 'Yes'
                            : 'No',
                },
                {
                    title: 'Reason No Dr',
                    value:
                        (actionDevice && actionDevice.details.dr_reason_no) ||
                        'No info',
                },
            ],
            [
                {
                    title: 'Drp Method',
                    value:
                        (actionDevice && actionDevice.details.drp_method) ||
                        'No info',
                },
            ],
        ]

        return sections.map((groupOfParams, index) => (
            <div key={index} className={styles.section}>
                {groupOfParams.map((param, index) => (
                    <div key={index}>
                        <div className={styles.sectionTitle}>{param.title}</div>
                        <div>{param.value || '-'}</div>
                    </div>
                ))}
            </div>
        ))
    }

    _renderActivityTabContent = () => {
        const { newComment, isScrolledTop } = this.state
        const { action, actionLogs = [] } = this.props
        const renderButtonLinks =
            (action.device || action.isAvZone) &&
            (action.monitored ||
                action.isAvZone ||
                isVirp(action) ||
                isSolution(action))

        return (
            <>
                {actionLogs.length > 0 ? (
                    <p className={styles.nrOfComments}>
                        Comments ({actionLogs.length})
                    </p>
                ) : null}

                <div
                    className={classNames(styles.topShadow, {
                        [styles.topShadowShow]: !isScrolledTop,
                        [styles.topShadowHide]: isScrolledTop,
                    })}
                >
                    <div />
                </div>

                <div
                    // Height of the container which renders the comments. This is done
                    // to be able to scroll on overflow
                    style={{
                        height: renderButtonLinks
                            ? 'calc(100vh - 296px)'
                            : 'calc(100vh - 256px)',
                    }}
                    className={styles.activityTabContent}
                    ref={this.commentsWrapperRef}
                >
                    <Comments
                        comments={this.parseComments(actionLogs)}
                        placeholderTitle="There are no comments here, yet."
                        placeholderSubtitle="Start typing in the comment field below."
                    />

                    <div className={styles.commentInputContainer}>
                        <InputGroup
                            value={newComment}
                            placeholder="Add a comment"
                            large
                            onChange={(e) =>
                                this.setState({ newComment: e.target.value })
                            }
                            onKeyDown={(e) =>
                                e.key === 'Enter'
                                    ? this.handleAddNewComment()
                                    : null
                            }
                            className={styles.commentInput}
                        />
                        <Button
                            intent="primary"
                            large
                            icon={<FontAwesomeIcon icon={faPaperPlane} />}
                            className={styles.sendCommentButton}
                            onClick={this.handleAddNewComment}
                        />
                    </div>
                </div>
            </>
        )
    }

    render() {
        const {
            action,
            isDrawerOpen,
            activeActionDetailsDrawerTab,
        } = this.props

        if (!action || !isDrawerOpen) return null

        return (
            <Drawer
                size={500}
                title={action.name}
                onClose={this.handleDrawerClose}
                isOpen={true}
            >
                <div className={styles.drawerContent}>
                    {this._renderButtonLinks()}

                    <ButtonTabs
                        id="action-details-tabs"
                        defaultActiveTab={activeActionDetailsDrawerTab}
                        onTabChange={(selectedTab) =>
                            this.setState({ activeTab: selectedTab })
                        }
                    >
                        <Tab
                            id="details"
                            title="Details"
                            panel={this._renderDetailsTabContent()}
                        />
                        <Tab
                            id="activity"
                            title="Activity"
                            panel={this._renderActivityTabContent()}
                        />
                    </ButtonTabs>
                </div>
            </Drawer>
        )
    }
}

export default connect(
    (state) => {
        const action = getAction(
            state,
            state.ui.runbook.activeRunbook,
            state.ui.runbook.controls.singleOpenAction
        )
        const actionDevice = getActionDevice(
            state,
            state.ui.runbook.activeRunbook,
            state.ui.runbook.controls.singleOpenAction
        )
        const runbook = getRunbook(state, state.ui.runbook.activeRunbook)
        const { ciToUid } = runbook.actions
        const uids = state.runbooks.byId[runbook.id]['actions']['uids']
        return {
            runbook: state.ui.runbook,
            singleOpenAction: state.ui.runbook.controls.singleOpenAction,
            action,
            actionDevice,
            attachedTo:
                actionDevice && ciToUid[actionDevice.details.attached_to],
            guestOfCluster:
                actionDevice && ciToUid[actionDevice.details.guest_of_cluster],
            nodeOfCluster:
                actionDevice && ciToUid[actionDevice.details.node_of_cluster],
            drPartner: actionDevice && ciToUid[actionDevice.details.dr_partner],
            logs: action && action.logs,
            treeParent:
                action && action.treeParent
                    ? getAction(
                          state,
                          state.ui.runbook.activeRunbook,
                          action.treeParent
                      )
                    : null,
            activeSession: runbook.activeSession,
            activeRunbook: state.ui.runbook.activeRunbook,
            isDrawerOpen: state.ui.runbook.controls.isDrawerOpen,
            activeActionDetailsDrawerTab:
                state.ui.runbook.controls.activeActionDetailsDrawerTab,
            uids,
            actionLogs: runbook.actions.actionLogs[action.uid]?.reverse(),
        }
    },
    {
        addCommentToAction,
        singleEditAction,
        toggleControl,
        getActionLogsFromServer,
        focusAction,
    }
)(ActionDetailsDrawer)
